import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, Feather } from "lucide-react";
import { ASSETS } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const LINES = ["Knowledge is the beginning.", "Legacy is the goal."];
const CHIPS = ["Knowledge", "Tools", "Services", "Legacy"];

const HeroSection = () => {
    const sectionRef = useRef(null);
    const frameRef = useRef(null);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 18 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 120, damping: 18 });

    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
    const bannerY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const glowOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0.2]);

    const onMouseMove = (e) => {
        const rect = frameRef.current?.getBoundingClientRect();
        if (!rect) return;
        mx.set((e.clientX - rect.left) / rect.width - 0.5);
        my.set((e.clientY - rect.top) / rect.height - 0.5);
    };

    const onMouseLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <section
            id="hero"
            ref={sectionRef}
            data-testid="hero-section"
            className="relative overflow-hidden pt-32 pb-20 sm:pt-40 lg:pt-44"
        >
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div className="absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-[#7200C8]/25 blur-[160px]" />
                <div className="absolute top-1/3 -left-40 h-[420px] w-[420px] rounded-full bg-[#D9A441]/12 blur-[140px]" />
                <div className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-[#7200C8]/15 blur-[130px]" />
            </div>

            <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
                <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10">
                    <div>
                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="flex items-center gap-4"
                        >
                            <span className="gold-hairline h-px w-12 sm:w-16" />
                            <p data-testid="hero-eyebrow" className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D9A441] sm:text-sm">
                                Education. Culture. Creation. Innovation.
                            </p>
                        </motion.div>

                        <h1
                            data-testid="hero-headline"
                            className="mt-8 font-display text-4xl font-black leading-[1.12] tracking-tight sm:text-5xl lg:text-6xl xl:text-[4.4rem]"
                        >
                            {LINES.map((line, i) => (
                                <span key={line} className="block overflow-hidden pb-1">
                                    <motion.span
                                        className={`block ${i === 1 ? "text-gold-gradient" : "text-[#F8F5EE]"}`}
                                        initial={{ y: "115%" }}
                                        animate={{ y: 0 }}
                                        transition={{ duration: 0.9, delay: 0.25 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
                                    >
                                        {line}
                                    </motion.span>
                                </span>
                            ))}
                        </h1>

                        <motion.p
                            data-testid="hero-subheading"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.85 }}
                            className="mt-7 max-w-xl text-base leading-relaxed text-[#F8F5EE]/70 sm:text-lg"
                        >
                            Welcome to the world of <span className="font-semibold text-[#F4C46B]">BrothaTruth</span> — a home for
                            Education, Culture, Creation, and Innovation that inspire you to move with purpose.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 1.1 }}
                            className="mt-10 flex flex-wrap items-center gap-4"
                        >
                            <button
                                data-testid="hero-cta-explore"
                                onClick={() => scrollToId("services")}
                                className="btn-shine group flex items-center gap-3 rounded-full bg-gold-gradient px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-[#080B12] shadow-[0_0_36px_rgba(217,164,65,0.4)] transition-transform duration-300 hover:scale-[1.04]"
                            >
                                Explore Our Services
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </button>
                            <button
                                data-testid="hero-cta-community"
                                onClick={() => scrollToId("community")}
                                className="group flex items-center gap-3 rounded-full border border-[#D9A441]/40 bg-white/[0.04] px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-[#F4C46B] backdrop-blur-sm transition-colors duration-300 hover:border-[#D9A441] hover:bg-[#D9A441]/10"
                            >
                                <Feather className="h-4 w-4" />
                                Join The Community
                            </button>
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 1.1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className="relative mx-auto w-full max-w-md lg:max-w-none"
                        style={{ perspective: 1400 }}
                    >
                        <motion.div
                            ref={frameRef}
                            data-testid="hero-portrait-frame"
                            onMouseMove={onMouseMove}
                            onMouseLeave={onMouseLeave}
                            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                            className="relative overflow-hidden rounded-[2rem] border border-[#D9A441]/40 shadow-[0_30px_90px_rgba(0,0,0,0.65),0_0_60px_rgba(217,164,65,0.18)]"
                        >
                            <img
                                data-testid="hero-portrait"
                                src={ASSETS.portrait}
                                alt="BrothaTruth — professional portrait in a navy double-breasted suit"
                                className="h-auto max-h-[620px] w-full object-cover object-top"
                                loading="eager"
                            />
                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#080B12]/70 via-transparent to-transparent" />
                            <div className="pointer-events-none absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-[#F4C46B]/25" />
                            <div className="absolute bottom-5 right-6 text-right">
                                <p className="font-display text-lg font-bold tracking-[0.22em] text-[#F4C46B]">BROTHATRUTH</p>
                                <p className="text-[10px] uppercase tracking-[0.3em] text-[#F8F5EE]/60">Educator. Builder. Legacy.</p>
                            </div>
                        </motion.div>

                        <motion.div
                            data-testid="hero-monogram-badge"
                            initial={{ opacity: 0, scale: 0.6, rotate: -10 }}
                            animate={{ opacity: 1, scale: 1, rotate: 0 }}
                            transition={{ duration: 0.8, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
                            className="animate-float-soft absolute -bottom-7 -left-5 flex h-24 w-24 items-center justify-center rounded-full bg-gold-gradient p-3 shadow-[0_14px_44px_rgba(217,164,65,0.5)] ring-4 ring-[#080B12] sm:-left-7 sm:h-28 sm:w-28"
                        >
                            <img
                                src={ASSETS.monogram}
                                alt="BrothaTruth BT monogram with feather cutout"
                                className="h-full w-full object-contain"
                            />
                        </motion.div>

                        <div className="pointer-events-none absolute -inset-8 -z-10 rounded-[2.5rem] bg-[#D9A441]/10 blur-3xl" aria-hidden="true" />
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 60, scale: 0.97 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    className="relative mt-24 sm:mt-28"
                >
                    <div
                        data-testid="hero-banner-frame"
                        className="relative overflow-hidden rounded-2xl border border-[#D9A441]/40 shadow-[0_30px_90px_rgba(0,0,0,0.65),0_0_60px_rgba(217,164,65,0.15)]"
                    >
                        <motion.img
                            src={ASSETS.banner}
                            alt="BrothaTruth banner — It's never about what you do, but how you do it — courthouse columns, historical books, golden scales and city skyline"
                            style={{ y: bannerY }}
                            className="h-auto w-full scale-[1.06] object-cover max-sm:scale-100"
                            loading="lazy"
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#080B12]/55 via-transparent to-transparent" />
                        <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-[#F4C46B]/25" />
                    </div>

                    <motion.div style={{ opacity: glowOpacity }} className="pointer-events-none absolute -inset-8 -z-10 rounded-[2rem] bg-[#D9A441]/10 blur-3xl" aria-hidden="true" />

                    <div className="pointer-events-none absolute inset-x-0 -bottom-5 hidden justify-center gap-3 sm:flex" aria-hidden="true">
                        {CHIPS.map((chip, i) => (
                            <motion.span
                                key={chip}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.2 + i * 0.12 }}
                                className="animate-float-soft rounded-full border border-[#D9A441]/40 bg-[#080B12]/85 px-5 py-2 font-display text-[11px] font-semibold uppercase tracking-[0.25em] text-[#F4C46B] backdrop-blur-md"
                                style={{ animationDelay: `${i * 0.6}s` }}
                            >
                                {chip}
                            </motion.span>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default HeroSection;
